import { useGrid } from "@/hooks/useGrid";
import { useDashboard } from "@/lib/store";
import { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { usePaneContext } from "../pane/paneContext";
import { useDepthOfMarket } from "@/hooks/useDepthOfMarket";
import { IOrderBook } from "@/hooks/useDepthOfMarket";
import { Input } from "../ui/input";
import { TradesBubbles } from "../shared/TradesBubbles";
import { SymbolSelect } from "../shared/SymbolSelect";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import numeral from "numeral";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTradesBySymbol } from "@/hooks/useTradesBySymbol";
import { useDepthOfMarketV2 } from "@/hooks/useDepthOfMarketV2";

interface IDOMRow {
  volume: number;
  price: number;
  type: "ask" | "bid" | "current";
  marginLevel?: number;
  cumulativeMarginLevel?: number;
}

// Format volume numbers
const formatVolume = (volume: number) => {
  return numeral(volume).format("0,0.0a").toUpperCase();
};

const formatPrice = (price: number, tickDecimals: number) => {
  // For very small numbers, use exponential notation
  if (tickDecimals > 8) {
    return price.toExponential(2);
  }

  // For regular numbers, ensure we don't lose precision
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: tickDecimals,
    maximumFractionDigits: tickDecimals,
    useGrouping: false,
  }).format(price);
};

const columnHelper = createColumnHelper<IDOMRow>();

const BID_COLOR = "rgb(60, 166, 75)"; // #3CA64B
const ASK_COLOR = "rgb(178, 24, 44)"; // #B2182C
const BID_BACKGROUND = "rgb(26, 88, 36)"; // #1A5824
const ASK_BACKGROUND = "rgb(96, 11, 23)"; // #600B17
const MAX_ORDER_COLOR = "rgb(255, 160, 40)"; // #FFA028

const getColorWithAlfa = (
  isAsk: boolean,
  alpha: number,
  isBackground: boolean = false,
) => {
  const color = isAsk
    ? isBackground
      ? ASK_BACKGROUND
      : ASK_COLOR
    : isBackground
      ? BID_BACKGROUND
      : BID_COLOR;
  return `rgba(${color.replace("rgb(", "").replace(")", "")}, ${alpha})`;
};

const isRoundNumber = (price: number, tickSize: number) => {
  const precision = tickSize.toString().split(".")[1]?.length || 0;
  const multiplier = Math.pow(10, precision);
  const scaledPrice = Math.round(price * multiplier);
  return scaledPrice % (multiplier / 100) === 0;
};

const isMaxVolume = (volume: number, maxVolume: number) => {
  const tolerance = 0.000001;
  return Math.abs(volume - maxVolume) < tolerance;
};

export function MarginLevelsDOMPaneBody() {
  const nodeId = usePaneContext();
  const { getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();
  const color = config.color;
  const [isCumulative, setIsCumulative] = useState(true);
  const [showUsd, setShowUsd] = useState(true);
  const [scale, setScale] = useState(1);
  const [stayCentered, setStayCentered] = useState(true);
  const { colorsToSymbolMap, setSymbolForColor } = useDashboard((state) => ({
    colorsToSymbolMap: state.colorsToSymbolMap,
    setSymbolForColor: state.setSymbolForColor,
  }));

  useEffect(() => {
    setIsCumulative(config?.isCumulative || false);
  }, [setIsCumulative, config?.isCumulative]);

  const nodeSymbol = colorsToSymbolMap[color];

  const { data } = useDepthOfMarket(nodeSymbol, scale) as {
    data: IOrderBook | undefined;
  };
  const { data: tradeInfo } = useTradesBySymbol(nodeSymbol);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const ROW_HEIGHT = 24;

  const columns = [
    columnHelper.accessor("volume", {
      cell: (info) => formatVolume(info.getValue()),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("price", {
      cell: (info) => formatPrice(info.getValue(), data?.tickDecimals || 0),
      footer: (info) => info.column.id,
    }),
  ];

  const preparedData = useMemo(() => {
    if (!data?.asks || !data?.bids) {
      return [];
    }

    const rows: IDOMRow[] = [];

    // Convert Maps to arrays and sort them
    const asks = Array.from(data.asks.entries()).sort((a, b) => a[0] - b[0]);

    const bids = Array.from(data.bids.entries()).sort((a, b) => b[0] - a[0]);

    // Process asks (sell orders) from bottom to top
    let cumVolume = 0;
    let cumMarginLevel = 0;
    for (const [price, values] of asks) {
      const volume = showUsd ? values.usdValue : values.size;
      cumVolume = isCumulative ? cumVolume + volume : volume;

      // Handle margin level
      const marginLevel = values.marginLevel || 0;
      cumMarginLevel = isCumulative
        ? cumMarginLevel + marginLevel
        : marginLevel;

      rows.unshift({
        price,
        volume: cumVolume,
        type: "ask",
        marginLevel,
        cumulativeMarginLevel: cumMarginLevel,
      });
    }

    // Add empty row for current price
    const currentPrice =
      asks.length > 0 ? asks[0][0] : bids.length > 0 ? bids[0][0] : 0;
    rows.push({
      price: currentPrice,
      volume: 0,
      type: "current",
    });

    // Process bids (buy orders)
    cumVolume = 0;
    cumMarginLevel = 0;
    for (const [price, values] of bids) {
      const volume = showUsd ? values.usdValue : values.size;
      cumVolume = isCumulative ? cumVolume + volume : volume;

      // Handle margin level
      const marginLevel = values.marginLevel || 0;
      cumMarginLevel = isCumulative
        ? cumMarginLevel + marginLevel
        : marginLevel;

      rows.push({
        price,
        volume: cumVolume,
        type: "bid",
        marginLevel,
        cumulativeMarginLevel: cumMarginLevel,
      });
    }

    return rows;
  }, [data, isCumulative, showUsd]);

  // Get the appropriate volume values based on the toggle
  const maxBidVolume = showUsd ? data?.maxBidUsdVolume : data?.maxBidVolume;
  const maxAskVolume = showUsd ? data?.maxAskUsdVolume : data?.maxAskVolume;
  const sumBidVolume = showUsd ? data?.sumBidUsdVolume : data?.sumBidVolume;
  const sumAskVolume = showUsd ? data?.sumAskUsdVolume : data?.sumAskVolume;

  // Calculate max margin level and cumulative margin levels
  const { maxMarginLevel, maxCumulativeMarginLevel } = useMemo(() => {
    if (!data?.asks || !data?.bids) {
      return { maxMarginLevel: 0, maxCumulativeMarginLevel: 0 };
    }
    let maxMargin = 0;
    let maxCumulative = 0;

    // Calculate max individual margin level
    data.asks.forEach((value) => {
      if (value.marginLevel > maxMargin) {
        maxMargin = value.marginLevel;
      }
    });
    data.bids.forEach((value) => {
      if (value.marginLevel > maxMargin) {
        maxMargin = value.marginLevel;
      }
    });

    // Calculate cumulative margin levels for the prepared data
    let askCumulative = 0;
    let bidCumulative = 0;

    // Process asks from top to bottom (same order as in preparedData)
    const askEntries = Array.from(data.asks.entries()).sort(
      (a, b) => a[0] - b[0],
    );
    for (const [_, values] of askEntries) {
      // Always add the margin level, even if it's zero
      askCumulative += values.marginLevel || 0;
      if (askCumulative > maxCumulative) {
        maxCumulative = askCumulative;
      }
    }

    // Process bids from top to bottom (same order as in preparedData)
    const bidEntries = Array.from(data.bids.entries()).sort(
      (a, b) => b[0] - a[0],
    );
    for (const [_, values] of bidEntries) {
      // Always add the margin level, even if it's zero
      bidCumulative += values.marginLevel || 0;
      if (bidCumulative > maxCumulative) {
        maxCumulative = bidCumulative;
      }
    }

    // Ensure we have a non-zero value for maxCumulativeMarginLevel
    // If all margin levels are zero, set a default value
    if (maxCumulative === 0) {
      maxCumulative = 1000; // Default value for scaling
    }

    return {
      maxMarginLevel: maxMargin,
      maxCumulativeMarginLevel: maxCumulative,
    };
  }, [data?.asks, data?.bids]);

  const tableInstance = useReactTable({
    data: preparedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = tableInstance.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  // Find the index of the current price row
  const currentPriceIndex = useMemo(
    () => rows.findIndex((row) => row.original.type === "current"),
    [rows],
  );

  const centerView = useCallback(() => {
    if (currentPriceIndex >= 0 && tableContainerRef.current) {
      const containerHeight = tableContainerRef.current.clientHeight;
      const targetScrollTop =
        currentPriceIndex * ROW_HEIGHT - containerHeight / 2;
      tableContainerRef.current.scrollTop = targetScrollTop;
    }
  }, [currentPriceIndex]);

  // Center when data updates if stayCentered is true
  useEffect(() => {
    if (stayCentered) {
      centerView();
    }
  }, [stayCentered, centerView, data]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only trigger if left shift is pressed and no input/textarea is focused
      if (
        event.code === "ShiftLeft" &&
        document.activeElement?.tagName !== "INPUT" &&
        document.activeElement?.tagName !== "TEXTAREA"
      ) {
        event.preventDefault();
        setStayCentered(true);
        centerView();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [centerView]);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      setStayCentered(false);
    };

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener("wheel", handleScroll, { passive: true });
      container.addEventListener("touchmove", handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener("wheel", handleScroll);
        container.removeEventListener("touchmove", handleScroll);
      }
    };
  }, []);

  // Center only on initial load
  useEffect(() => {
    centerView();
  }, [centerView]); // Empty dependency array for initial load only

  if (!data) {
    return <div>Loading...</div>;
  }

  const totalSize = rowVirtualizer.getTotalSize();

  // Find the spread index (where asks end and bids begin)
  const spreadIndex = rows.findIndex((row) => row.original.type === "bid");
  const containerHeight = tableContainerRef.current?.clientHeight || 0;

  // Calculate the offset to center the spread
  const centerOffset = Math.max(
    0,
    containerHeight / 2 - spreadIndex * ROW_HEIGHT,
  );

  // Calculate percentages using the selected volumes
  const totalMaxVolume = Math.max(maxAskVolume ?? 0, maxBidVolume ?? 0);
  const totalSumVolume = Math.max(sumAskVolume ?? 0, sumBidVolume ?? 0);
  const askMaxPercentage = ((maxAskVolume ?? 0) / totalMaxVolume) * 100;
  const bidMaxPercentage = ((maxBidVolume ?? 0) / totalMaxVolume) * 100;
  const askSumPercentage = ((sumAskVolume ?? 0) / totalSumVolume) * 100;
  const bidSumPercentage = ((sumBidVolume ?? 0) / totalSumVolume) * 100;

  return (
    <div className="h-full flex flex-col">
      {/* Controls */}
      <div className="flex justify-between items-center px-2 py-1 text-xs text-gray-500">
        <div className="flex items-center justify-between w-full">
          <div className="flex gap-2">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={showUsd}
                onChange={(e) => setShowUsd(e.target.checked)}
                className="form-checkbox h-3 w-3"
              />
              <span>USD</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={isCumulative}
                onChange={(e) => setIsCumulative(e.target.checked)}
                className="form-checkbox h-3 w-3"
              />
              <span>Cumulative</span>
            </label>
          </div>
          <label className="flex items-center gap-1">
            <span>Scale</span>
            <Input
              type="number"
              value={scale}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value) && value > 0) {
                  setScale(value);
                }
              }}
              min="1"
              step="1"
              className="w-16 text-xxs h-5 rounded"
            />
            <button
              onClick={centerView}
              className="px-2 py-0.5 text-xxs bg-gray-800 hover:bg-gray-700 rounded"
            >
              Center
            </button>
            <label className="flex items-center gap-1 cursor-pointer ml-2">
              <input
                type="checkbox"
                checked={stayCentered}
                onChange={(e) => setStayCentered(e.target.checked)}
                className="form-checkbox h-3 w-3"
              />
              <span className="text-xs">Centered</span>
            </label>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <Popover>
              <PopoverTrigger asChild>
                <div className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:opacity-50">
                  {nodeSymbol || "Select Symbol"}
                  {data && (
                    <span className="text-gray-500">
                      | {data.tickSize} | {data.tickDecimals}
                    </span>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[200px]" align="start">
                <SymbolSelect
                  value={nodeSymbol}
                  onValueChange={(value) => {
                    setSymbolForColor(color, value);
                  }}
                />
              </PopoverContent>
            </Popover>
          </label>
        </div>
      </div>

      {/* Summary Progress Bars */}
      <div className="flex flex-col gap-1 mb-1">
        {/* Max Volume Bar */}
        <div className="flex flex-col gap-0.5">
          <div className="flex justify-between text-xs px-2">
            <span style={{ color: getColorWithAlfa(false, 1) }}>
              {formatVolume(maxBidVolume ?? 0)}
            </span>
            <span className="text-xs text-gray-500">Max</span>
            <span style={{ color: getColorWithAlfa(true, 1) }}>
              {formatVolume(maxAskVolume ?? 0)}
            </span>
          </div>
          <div className="flex h-1 bg-gray-200">
            <div
              style={{
                width: `${bidMaxPercentage}%`,
                height: "100%",
                backgroundColor: getColorWithAlfa(false, bidMaxPercentage / 10),
              }}
            />
            <div
              style={{
                width: `${askMaxPercentage}%`,
                height: "100%",
                backgroundColor: getColorWithAlfa(true, askMaxPercentage / 10),
              }}
            />
          </div>
        </div>

        {/* Sum Volume Bar */}
        <div className="flex flex-col gap-0.5">
          <div className="flex justify-between text-xs px-2">
            <span style={{ color: getColorWithAlfa(false, 1) }}>
              {formatVolume(sumBidVolume ?? 0)}
            </span>
            <span className="text-xs text-gray-500">Sum</span>
            <span style={{ color: getColorWithAlfa(true, 1) }}>
              {formatVolume(sumAskVolume ?? 0)}
            </span>
          </div>
          <div className="flex h-1 bg-gray-200">
            <div
              style={{
                width: `${bidSumPercentage}%`,
                height: "100%",
                backgroundColor: getColorWithAlfa(false, bidSumPercentage / 10),
              }}
            />
            <div
              style={{
                width: `${askSumPercentage}%`,
                height: "100%",
                backgroundColor: getColorWithAlfa(true, askSumPercentage / 10),
              }}
            />
          </div>
        </div>
      </div>

      {/* Order Book */}
      <div ref={tableContainerRef} className="flex-1 overflow-auto relative">
        <div
          style={{
            height: `${totalSize}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            const isAsk = row.original.type === "ask";
            const isCurrent = row.original.type === "current";

            if (isCurrent) {
              return (
                <div
                  key={virtualRow.index}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  className="absolute top-0 left-0 w-full  grid text-xs"
                  style={{
                    transform: `translateY(${virtualRow.start}px)`,
                    height: `${ROW_HEIGHT * 1.5}px`,
                    gridTemplateColumns: "1fr 80px 1fr",
                  }}
                ></div>
              );
            }

            const maxVolume = isAsk ? maxAskVolume : maxBidVolume;
            const volumePercentage =
              (row.original.volume / (maxVolume ?? 1)) * 100;
            const marginLevel =
              data?.asks.get(row.original.price)?.marginLevel ||
              data?.bids.get(row.original.price)?.marginLevel ||
              0;

            // Check if we need to insert TradesBubbles
            const nextRow = rows[virtualRow.index + 1];
            const showTradesBubbles =
              row.original.type === "ask" && nextRow?.original.type === "bid";

            return (
              <div key={virtualRow.index} className="w-full">
                <div
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  className="absolute top-0 left-0 w-full grid text-xs hover:bg-gray-800/30"
                  style={{
                    transform: `translateY(${virtualRow.start}px)`,
                    height: `${ROW_HEIGHT}px`,
                    color: isAsk
                      ? getColorWithAlfa(true, 1)
                      : getColorWithAlfa(false, 1),
                    gridTemplateColumns: "1fr 80px 1fr",
                  }}
                  onMouseEnter={() => setHoveredRow(virtualRow.index)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {/* Volume bar column */}
                  <div className="relative px-2 py-1">
                    <div
                      style={{
                        position: "absolute",
                        height: "100%",
                        width: `${Math.min(volumePercentage, 100)}%`,
                        left: 0,
                        top: 0,
                        backgroundColor: isMaxVolume(
                          row.original.volume,
                          (isAsk ? maxAskVolume : maxBidVolume) ?? 0,
                        )
                          ? `rgba(${MAX_ORDER_COLOR.replace("rgb(", "").replace(")", "")}, 0.8)`
                          : getColorWithAlfa(isAsk, 0.8),
                        zIndex: 1,
                      }}
                    />
                    <div className="relative z-10 pl-1 text-white">
                      {formatVolume(row.original.volume)}
                    </div>
                  </div>

                  {/* Price column */}
                  <div
                    className={`px-2 py-1 text-center text-white ${isRoundNumber(row.original.price, data?.tickSize || 0.01) ? "font-bold" : ""}`}
                  >
                    {hoveredRow === virtualRow.index ? (
                      <span className="text-gray-400">
                        {(() => {
                          const currentPrice = Number(tradeInfo?.aggTrade?.p);
                          if (!currentPrice) {
                            return formatPrice(
                              row.original.price,
                              data?.tickDecimals || 0,
                            );
                          }
                          return (
                            (
                              (1 - currentPrice / row.original.price) *
                              100
                            ).toFixed(2) + "%"
                          );
                        })()}
                      </span>
                    ) : (
                      formatPrice(row.original.price, data?.tickDecimals || 0)
                    )}
                  </div>

                  {/* Margin level column */}
                  <div className="relative px-2 py-1">
                    {/* Always render the background for margin levels, even if zero */}
                    <div
                      style={{
                        position: "absolute",
                        height: "100%",
                        width: `${((row.original.cumulativeMarginLevel || 0) / (maxCumulativeMarginLevel || 1)) * 100}%`,
                        right: 0,
                        top: 0,
                        backgroundColor: isAsk
                          ? getColorWithAlfa(false, 0.5)
                          : getColorWithAlfa(true, 0.5),
                        zIndex: 2,
                      }}
                    />
                    {/* Only show the text if there's an actual margin level */}
                    {(row.original.marginLevel || 0) > 0 && (
                      <div
                        className="relative z-10 pr-1 text-white"
                        style={{ textAlign: "right" }}
                      >
                        {(() => {
                          const currentPrice = Number(tradeInfo?.aggTrade?.p);
                          if (!currentPrice) {
                            return `$${formatVolume(row.original.marginLevel || 0)}`;
                          }
                          const priceDiff = Math.abs(
                            (row.original.price / currentPrice - 1) * 100,
                          ).toFixed(2);
                          return `$${formatVolume(row.original.marginLevel || 0)}`;
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
